import React from "react";
import Modal2 from "../Modal";
import { useAccount, useSwitchChain } from "wagmi";
import { parseUnits } from "viem";
import { useAuctionManager } from "../../hooks/useAuctionManager";
import { createListing } from "../../lib/api";
import { resolveChainRow, chainRowToChainId } from "../../lib/resolveChainRow";

/**
 * SellItemModal — listing form for a token you own. Covers all six shapes the
 * AuctionManager supports, picked from (sale type) x (token standard):
 *
 *                 ERC-721                        ERC-1155
 *   Fixed price   createSaleListing              createERC1155SaleListing
 *   English auc.  createAuction                  createERC1155Auction
 *   Dutch auc.    createDutchAuction             createERC1155DutchAuction
 *
 * Two differences drive the branching, and both are contract requirements, not
 * UI preferences:
 *
 * 1) APPROVAL. 721 requires `getApproved(tokenId) == diamond` — a per-token
 *    `approve`. 1155 requires `isApprovedForAll(seller, diamond)` — a blanket
 *    operator grant — because listings move a *quantity* of a class, which a
 *    per-token approval cannot express. 1155 listing also ESCROWS the quantity
 *    (`safeTransferFrom` into the diamond); a 721 fixed-price listing does not,
 *    but both 721 auction types do.
 *
 * 2) QUANTITY. 1155 has no tokenId, only a class id plus a quantity. All three
 *    1155 creators take `quantity`; none of the 721 creators do.
 *
 * The standard is detected on-chain via the balanceOf probe already implemented
 * as `detectTokenStandard`, not guessed from the collection, so a mislabelled
 * collection still routes to a call that can succeed.
 */
export type SaleType = "fixed" | "english" | "dutch";

export type SellItemTarget = {
  /** NFT contract holding the token. */
  contractAddress: string;
  /** ERC-721 token id (or the class id for 1155). */
  tokenId?: string | number;
  /** chainData index OR chain name OR EVM chain id — see resolveChainRow. */
  chainIndex?: number | string;
  /** Explicit EVM chain id; wins over chainIndex when derivable. */
  chainId?: number;
  /** Collection id, needed by the backend's listItem call. */
  collectionId?: string | number;
  /** Token metadata, forwarded to the backend alongside the auction data. */
  meta?: Record<string, any>;
  /** Skip the on-chain standard probe when the caller already knows it. */
  tokenStandard?: "721" | "1155";
};

/** Seconds -> "1d 2h 30m", for the auction duration summary. */
function fmtDuration(totalSecs: number): string {
  if (!totalSecs || totalSecs <= 0) return "—";
  const d = Math.floor(totalSecs / 86400);
  const h = Math.floor((totalSecs % 86400) / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const parts: string[] = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  return parts.length ? parts.join(" ") : `${totalSecs}s`;
}

const SALE_TYPES: { id: SaleType; label: string; blurb: string }[] = [
  { id: "fixed", label: "Fixed price", blurb: "Sell immediately at a set price." },
  { id: "english", label: "English auction", blurb: "Bidders compete over a set duration." },
  { id: "dutch", label: "Dutch auction", blurb: "Price falls from start to reserve over time." },
];

const SellItemModal = ({
  show,
  onClose,
  title,
  target,
  onListed,
}: {
  show?: boolean;
  onClose?: () => void;
  title?: any;
  target?: SellItemTarget | null;
  /** Fired after the chain listing succeeds + the backend is updated. */
  onListed?: (listingId: string) => void;
}) => {
  let account: string | undefined;
  let isConnected = false;
  let walletChainId: number | undefined;
  let switchChainFn: ((opts: { chainId: any }) => void) | undefined;
  let isSwitching = false;
  try {
    const acct = useAccount();
    account = acct.address;
    isConnected = acct.isConnected;
    walletChainId = acct.chainId;
    const sc = useSwitchChain();
    switchChainFn = sc.switchChain;
    isSwitching = sc.isPending;
  } catch {
    // outside WagmiProvider during initial mount — keep safe defaults
  }

  const chainRow = React.useMemo(
    () => resolveChainRow({ chainIndex: target?.chainIndex, chainId: target?.chainId }),
    [target?.chainIndex, target?.chainId]
  );
  const readChainId = target?.chainId ?? chainRowToChainId(chainRow);
  const am = useAuctionManager(undefined, { chainId: readChainId });

  const tokenSymbol = chainRow?.tokName || "COIN";

  // The client that must be used to WAIT for our own transactions.
  //
  // `am.publicClient` is bound to the READ chain (opts.chainId — the chain the
  // TOKEN lives on), while every write is broadcast through the CONNECTED
  // wallet's chain. When those differ, waiting on `publicClient` polls a chain
  // that can never contain the hash, so waitForTransactionReceipt hangs
  // forever with no error — the modal just spins on "Waiting for…".
  //
  // `walletChainClient` is the connected chain's client, which is exactly
  // where the hash landed. Reads still go through `publicClient` (correct: the
  // token may genuinely live elsewhere), only receipt-waiting uses this.
  const writeChainClient: any = am.walletChainClient || am.publicClient;

  const contractAddress = target?.contractAddress;
  const tokenId = target?.tokenId;

  const [saleType, setSaleType] = React.useState<SaleType>("fixed");
  const [std, setStd] = React.useState<"721" | "1155" | null>(target?.tokenStandard ?? null);
  const [price, setPrice] = React.useState("");
  const [reserve, setReserve] = React.useState("");
  const [quantity, setQuantity] = React.useState("1");
  const [days, setDays] = React.useState("1");
  const [hours, setHours] = React.useState("0");
  const [minutes, setMinutes] = React.useState("0");
  const [busy, setBusy] = React.useState(false);
  const [step, setStep] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);

  // Reset the form each time the modal opens so a previous token's price, sale
  // type or error never leaks into the next one.
  React.useEffect(() => {
    if (!show) return;
    setSaleType("fixed");
    setStd(target?.tokenStandard ?? null);
    setPrice("");
    setReserve("");
    setQuantity("1");
    setDays("1");
    setHours("0");
    setMinutes("0");
    setErr(null);
    setStep("");
    setBusy(false);
  }, [show, contractAddress, String(tokenId)]);

  // Detect the token standard once per (contract, token). ERC-1155 exposes
  // balanceOf(address,uint256); the 721 wrapper has only single-arg balanceOf,
  // which reverts — that revert IS the discriminator the hook relies on.
  React.useEffect(() => {
    if (!show || !contractAddress || tokenId === undefined || tokenId === null) return;
    if (target?.tokenStandard) { setStd(target.tokenStandard); return; }
    if (std) return;
    let dead = false;
    (async () => {
      try {
        const detected = await am.detectTokenStandard(contractAddress, BigInt(tokenId));
        if (!dead && detected) setStd(detected);
      } catch {
        // Undetectable: leave null and block submission rather than guess —
        // calling a 721 creator against a 1155 contract just reverts.
      }
    })();
    return () => { dead = true; };
  }, [show, contractAddress, String(tokenId), std, target?.tokenStandard]);

  const parseAmount = (raw: string): bigint | null => {
    const s = (raw || "").trim();
    if (!s || !/^\d*\.?\d*$/.test(s) || s === ".") return null;
    try {
      const v = parseUnits(s, 18);
      return v > 0n ? v : null;
    } catch {
      return null;
    }
  };

  const typedPrice = parseAmount(price);
  const typedReserve = saleType === "dutch" ? parseAmount(reserve) : typedPrice;
  const priceInvalid = price.trim() !== "" && typedPrice === null;
  const reserveInvalid = saleType === "dutch" && reserve.trim() !== "" && typedReserve === null;

  const durationSecs =
    (parseInt(days || "0", 10) || 0) * 86400 +
    (parseInt(hours || "0", 10) || 0) * 3600 +
    (parseInt(minutes || "0", 10) || 0) * 60;

  const parsedQty = React.useMemo(() => {
    const q = (quantity || "").trim();
    if (!/^\d+$/.test(q)) return null;
    try {
      const v = BigInt(q);
      return v > 0n ? v : null;
    } catch {
      return null;
    }
  }, [quantity]);
  const qtyInvalid = quantity.trim() !== "" && parsedQty === null;

  const is1155 = std === "1155";
  const isAuction = saleType === "english" || saleType === "dutch";

  // Validation kept explicit so the disabled button and the inline hints can
  // never disagree about why submission is blocked.
  const dutchReserveInvalid =
    saleType === "dutch" && (typedReserve === null || (typedPrice !== null && typedReserve >= typedPrice));
  const wrongChain = Boolean(readChainId && walletChainId && readChainId !== walletChainId);
  const onSwitchChain = () => {
    if (readChainId) switchChain({ chainId: readChainId as any });
  };

  // A receipt wait must never be able to hang the modal forever. viem's
  // `waitForTransactionReceipt` polls indefinitely with no ceiling, so a
  // transport that silently never resolves (dead IPv6 endpoint, unreachable
  // explorer, RPC that drops the request) strands the UI on
  // "Waiting for confirmation…" with no way out and no error. Race it against
  // a deadline; on timeout we report honestly rather than claiming failure,
  // because the transaction may well still be mined on-chain.
  const withReceiptDeadline = async (hash: `0x${string}`, client: any, what: string) => {
    const RECEIPT_DEADLINE_MS = 120_000;
    let timer: any;
    try {
      return await Promise.race([
        client.waitForTransactionReceipt({ hash }),
        new Promise((_resolve, reject) => {
          timer = setTimeout(
            () =>
              reject(
                new Error(
                  `Timed out after ${RECEIPT_DEADLINE_MS / 1000}s waiting for the ${what} receipt. ` +
                    `The transaction was sent as ${hash} — it may still be mined. ` +
                    `Check your wallet or the block explorer before retrying.`,
                ),
              ),
            RECEIPT_DEADLINE_MS,
          );
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  };

  const canSubmit =
    isConnected &&
    !busy &&
    !wrongChain &&
    Boolean(std) &&
    typedPrice !== null &&
    !dutchReserveInvalid &&
    (!isAuction || durationSecs > 0) &&
    (!is1155 || parsedQty !== null) &&
    Boolean(contractAddress) &&
    tokenId !== undefined &&
    tokenId !== null;

  // A 721 fixed-price listing is approval-only (the NFT stays in the wallet);
  // every other shape escrows into the diamond. Say so, because it changes what
  // the seller is agreeing to.
  const escrows = is1155 ? true : saleType !== "fixed";

  const submit = async () => {
    if (!canSubmit || !contractAddress || tokenId === undefined || tokenId === null) return;
    // Both clients are undefined until wagmi has mounted. `submit` is only
    // reachable from a connected wallet, so this is defensive — but report it
    // clearly instead of throwing a null dereference mid-flow.
    if (!writeChainClient) {
      setErr("No client available for the connected network yet — please try again.");
      return;
    }
    const tokId = BigInt(tokenId);
    const amount = typedPrice as bigint;
    const qty = is1155 ? (parsedQty as bigint) : undefined;

    setBusy(true);
    setErr(null);
    try {
      // ---- 1) Approval -------------------------------------------------
      // 721: per-token `approve(diamond, tokenId)`.
      // 1155: blanket `setApprovalForAll(diamond, true)` — required because the
      //       creators move a quantity of a class, which no per-token approval
      //       can express. Re-granting an existing grant is a wasted tx.
      setStep("Checking approval…");
      let needsApproval = true;
      if (is1155) {
        try {
          const already: any = await am.publicClient!.readContract({
            address: contractAddress as `0x${string}`,
            abi: [{
              name: "isApprovedForAll",
              type: "function",
              inputs: [
                { name: "account", type: "address" },
                { name: "operator", type: "address" },
              ],
              outputs: [{ name: "", type: "bool" }],
              stateMutability: "view",
            }] as any,
            functionName: "isApprovedForAll",
            args: [account as `0x${string}`, am.proxyAddress as `0x${string}`],
          } as any);
          needsApproval = already !== true;
        } catch {
          // Unreadable — attempt the approval and let the contract decide.
        }
      } else {
        try {
          const approved: any = await am.publicClient!.readContract({
            address: contractAddress as `0x${string}`,
            abi: [{
              name: "getApproved",
              type: "function",
              inputs: [{ name: "tokenId", type: "uint256" }],
              outputs: [{ name: "", type: "address" }],
              stateMutability: "view",
            }] as any,
            functionName: "getApproved",
            args: [tokId],
          } as any);
          needsApproval =
            String(approved || "").toLowerCase() !== String(am.proxyAddress || "").toLowerCase();
        } catch {
          // Unreadable — attempt the approval and let the contract decide.
        }
      }

      if (needsApproval) {
        setStep("Approve in your wallet…");
        const hash = is1155
          ? await am.approveNFTForAll(contractAddress, true)
          : await am.approveNFT(contractAddress, tokId);
        if (!hash) throw new Error(am.error || "Approval transaction was not sent.");
        setStep("Waiting for approval…");
        // `approveNFT`/`approveNFTForAll` do NOT await the receipt internally
        // (unlike `callWrite`), so this wait is genuinely required. The
        // deadline guarantees it surfaces an error rather than hanging.
        await withReceiptDeadline(hash, writeChainClient, "approval");
        if (am.error) throw new Error(am.error);
      }

      // ---- 2) Create the listing ---------------------------------------
      // Each branch matches its creator's exact signature; a mis-ordered
      // argument silently creates a listing with the wrong reserve/duration,
      // so these stay adjacent to the call rather than factored out.
      //
      // These go through `callWriteWithReceipt`, NOT the six named wrappers:
      // `callWrite` already awaits the receipt internally, and re-waiting here
      // polls `writeChainClient` a second time. When the wallet sits on a
      // different chain than the collection's read chain that second poll can
      // target a chain that will never contain the hash — which never rejects,
      // it just hangs on "Waiting for confirmation…" forever even though the
      // receipt was already resolved. Using the returned receipt removes that
      // entire class of failure.
      setStep("Creating listing…");
      let receipt: any = null;
      let listingId: string | null = null;
      if (is1155) {
        if (saleType === "fixed") {
          receipt = await am.callWriteWithReceipt("createERC1155SaleListing", [contractAddress, tokId, qty!, amount]);
        } else if (saleType === "english") {
          // finalizeOnReserveMet=false -> a real timed English auction that ends
          // on finishTime. true would make it an instant "buy it now" at
          // reserve, which is what the `fixed` type above already covers.
          receipt = await am.callWriteWithReceipt("createERC1155Auction", [contractAddress, tokId, qty!, BigInt(durationSecs), amount, false]);
        } else {
          receipt = await am.callWriteWithReceipt("createERC1155DutchAuction", [contractAddress, tokId, qty!, amount, typedReserve as bigint, BigInt(durationSecs)]);
        }
      } else if (saleType === "fixed") {
        receipt = await am.callWriteWithReceipt("createSaleListing", [contractAddress, tokId, amount]);
      } else if (saleType === "english") {
        receipt = await am.callWriteWithReceipt("createAuction", [contractAddress, tokId, BigInt(durationSecs), amount, false]);
      } else {
        receipt = await am.callWriteWithReceipt("createDutchAuction", [contractAddress, tokId, amount, typedReserve as bigint, BigInt(durationSecs)]);
      }
      if (!receipt) throw new Error(am.error || "Listing transaction was not sent.");

      // ---- 3) Recover the new id ---------------------------------------
      // Every creator draws its id from the SAME counter (ds.auctionID) and
      // emits newListing(auctionId, nftContract, tokenId, listedBy, reserve)
      // with auctionId indexed — so one decode covers all six paths. The hook
      // already decoded it against the facet ABI; the raw-topic scan below is
      // the fallback for the 1155 facets, whose ABI may not match the emitter.
      const NEW_LISTING_SIG = "newListing(uint256,address,uint256,address,uint256)";
      listingId = receipt.decodedEventId || null;
      if (!listingId) {
        try {
          const { toEventSelector } = await import("viem");
          const topic = (toEventSelector(NEW_LISTING_SIG) as string).toLowerCase();
          for (const log of (receipt?.receipt?.logs || []) as any[]) {
            if (!log?.topics || log.topics.length < 2) continue;
            if (String(log.topics[0]).toLowerCase() !== topic) continue;
            const raw = String(log.topics[1] || "");
            if (raw.length >= 66) listingId = BigInt("0x" + raw.slice(26)).toString();
            break;
          }
        } catch {
          // viem import failed — fall through to the error below.
        }
      }
      if (!listingId) {
        throw new Error("Listing created but its id could not be read; the backend was not updated.");
      }

      // ---- 4) Mirror into the backend ----------------------------------
      // So the grid's auctionID cache (and thus the "For Sale" badge) reflects
      // the new listing IMMEDIATELY, without waiting for the chainWatch webhook
      // or the backend's 30s auctionPoll reconciler. A backend hiccup must not
      // invalidate a chain listing that already succeeded, so it is reported
      // rather than thrown.
      setStep("Updating listing index…");
      const listPayload = {
        // Decoded newListing args — the authoritative identity of the listing:
        // auctionId (indexed), nftContract, tokenId, listedBy, reserveAmount.
        event: {
          auctionId: String(listingId),
          nftContract: contractAddress,
          tokenId: String(tokenId),
          listedBy: account,
          reserveAmount: amount.toString(),
        },
        // Diamond proxy — the backend reads endTime through it for auction types.
        contractAddr: am.proxyAddress,
        saleType,
        chainId: readChainId,
        meta: target?.meta,
      };
      console.log("[auction] listing payload ->", JSON.stringify(listPayload, null, 2));
      try {
        await createListing(listPayload);
      } catch (e: any) {
        setErr(
          "Listed on-chain, but the listing index could not be updated (" +
            (e?.message || "backend error") +
            "). The item will show as unlisted until it is re-indexed."
        );
        setBusy(false);
        setStep("");
        return;
      }

      onListed?.(listingId);
      onClose?.();
    } catch (e: any) {
      setErr(am.error || e?.shortMessage || e?.message || "Failed to create the listing.");
      setStep("");
    } finally {
      setBusy(false);
    }
  };

  // ------------------------------------------------------------------ inputs
  const inputStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    padding: "9px 11px",
    fontFamily: "monospace",
    fontSize: "0.9rem",
    color: "#e8ecf5",
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.14)",
    borderRadius: 4,
    outline: "none",
  };
  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.78rem",
    color: "#aeb6c8",
    marginBottom: "5px",
  };
  const hintStyle: React.CSSProperties = {
    marginTop: "5px",
    fontSize: "0.75rem",
    color: "#e08a80",
  };

  const numInput = (
    id: string,
    label: string,
    value: string,
    onChange: (v: string) => void,
    invalid = false,
    placeholder = "0.0"
  ) => (
    <div style={{ marginBottom: "10px" }}>
      <label htmlFor={id} style={labelStyle}>{label}</label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        disabled={busy}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, borderColor: invalid ? "#b4453c" : "rgba(255,255,255,0.14)" }}
      />
      {invalid && <div style={hintStyle}>Enter a valid amount, e.g. 1.25</div>}
    </div>
  );

  const content = (
    <div>
      {/* Sale type. Dutch and English take materially different arguments, so
          the choice drives which fields below are shown. */}
      <div style={{ marginBottom: "14px" }}>
        <div style={{ ...labelStyle, marginBottom: "7px" }}>Sale type</div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {SALE_TYPES.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={busy}
              onClick={() => setSaleType(t.id)}
              title={t.blurb}
              style={{
                flex: "1 1 30%",
                minWidth: "96px",
                padding: "8px 10px",
                fontSize: "0.74rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                cursor: busy ? "default" : "pointer",
                color: saleType === t.id ? "#fff" : "#aeb6c8",
                background: saleType === t.id ? "#14548d" : "rgba(255,255,255,0.04)",
                border: `1px solid ${saleType === t.id ? "#2f7fd4" : "rgba(255,255,255,0.14)"}`,
                borderRadius: 6,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ marginTop: "6px", fontSize: "0.75rem", color: "#7d8ba5" }}>
          {SALE_TYPES.find((t) => t.id === saleType)?.blurb}
        </div>
      </div>

      {!std && (
        <div
          style={{
            marginBottom: "12px",
            padding: "9px 11px",
            fontSize: "0.78rem",
            color: "#e0b060",
            background: "rgba(224,176,96,0.1)",
            border: "1px solid rgba(224,176,96,0.35)",
            borderRadius: 4,
          }}
        >
          Detecting token standard…
        </div>
      )}

      {/* Quantity is 1155-only: a class id has no single "token", so the seller
          chooses how many units of the lot to list. */}
      {is1155 && (
        <div style={{ marginBottom: "10px" }}>
          <label htmlFor="sim-qty-input" style={labelStyle}>Quantity</label>
          <input
            id="sim-qty-input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="1"
            value={quantity}
            disabled={busy}
            onChange={(e) => setQuantity(e.target.value)}
            style={{
              ...inputStyle,
              borderColor: qtyInvalid ? "#b4453c" : "rgba(255,255,255,0.14)",
            }}
          />
          {qtyInvalid && <div style={hintStyle}>Enter a whole number greater than zero.</div>}
        </div>
      )}

      {saleType === "dutch" ? (
        numInput("sim-start-input", `Start price (${tokenSymbol})`, price, setPrice, priceInvalid)
      ) : (
        numInput(
          "sim-price-input",
          saleType === "english" ? `Reserve price (${tokenSymbol})` : `Price (${tokenSymbol})`,
          price,
          setPrice,
          priceInvalid
        )
      )}

      {saleType === "dutch" && (
        <>
          {numInput("sim-reserve-input", `Reserve price (${tokenSymbol})`, reserve, setReserve, reserveInvalid)}
          <div
            style={{
              marginTop: "-4px",
              marginBottom: "12px",
              fontSize: "0.75rem",
              color: dutchReserveInvalid && reserve.trim() ? "#e0b060" : "#7d8ba5",
            }}
          >
            The reserve must be lower than the start price.
          </div>
        </>
      )}

      {isAuction && (
        <>
          <div style={{ ...labelStyle, marginBottom: "5px" }}>Auction duration</div>
          <div style={{ display: "flex", gap: "8px", marginBottom: "6px" }}>
            <input
              id="sim-days" type="number" min="0" max="365" disabled={busy}
              value={days} onChange={(e) => setDays(e.target.value)}
              style={{ ...inputStyle, textAlign: "center" }} aria-label="Days"
            />
            <input
              id="sim-hours" type="number" min="0" max="23" disabled={busy}
              value={hours} onChange={(e) => setHours(e.target.value)}
              style={{ ...inputStyle, textAlign: "center" }} aria-label="Hours"
            />
            <input
              id="sim-minutes" type="number" min="0" max="59" disabled={busy}
              value={minutes} onChange={(e) => setMinutes(e.target.value)}
              style={{ ...inputStyle, textAlign: "center" }} aria-label="Minutes"
            />
          </div>
          <div
            style={{
              marginBottom: "12px",
              fontSize: "0.75rem",
              color: durationSecs > 0 ? "#7d8ba5" : "#e0b060",
            }}
          >
            Days / hours / minutes — ends in {fmtDuration(durationSecs)}.
          </div>
        </>
      )}

      <div style={{ fontSize: "0.78rem", color: "#aeb6c8", lineHeight: 1.45, marginBottom: "4px" }}>
        {escrows
          ? "The listed quantity is held by the marketplace contract until the sale settles."
          : "The marketplace is approved for this token only; the NFT stays in your wallet until a buyer accepts."}
      </div>

      {err && (
        <div
          style={{
            marginTop: "12px",
            padding: "9px 11px",
            fontSize: "0.78rem",
            color: "#e0a49c",
            background: "rgba(180,69,60,0.12)",
            border: "1px solid rgba(180,69,60,0.4)",
            borderRadius: 4,
            wordBreak: "break-word",
          }}
        >
          {err}
        </div>
      )}

      <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
        {wrongChain ? (
          <button
            className="pixel-btn"
            style={{ width: "100%" }}
            onClick={onSwitchChain}
            disabled={isSwitching}
          >
            {isSwitching ? "Switching…" : `Switch to ${chainRow?.network || `chain ${readChainId}`}`}
          </button>
        ) : (
          <button
            className="pixel-btn"
            style={{ width: "100%", opacity: canSubmit ? 1 : 0.5 }}
            disabled={!canSubmit}
            onClick={submit}
          >
            {!isConnected
              ? "Connect wallet"
              : busy
                ? step || "Working…"
                : saleType === "fixed"
                  ? "List for sale"
                  : saleType === "english"
                    ? "Start English auction"
                    : "Start Dutch auction"}
          </button>
        )}
        <button
          className="pixel-btn"
          style={{ width: "100%", background: "transparent", border: "1px solid rgba(255,255,255,0.12)", color: "#aeb6c8" }}
          onClick={onClose}
          disabled={busy}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <Modal2
      show={show}
      modalTitle={title || "List for sale"}
      content={content}
      handleClose={onClose}
      panelClassName="pixel-modal-sale"
    />
  );
};

export default SellItemModal;
